import {Controller} from "stimulus"

// Drives CKEditor 5 in place of the legacy CKEditor 4. It wraps the (name-bound) textarea
// that the form submits: the textarea is hidden, a <ckeditor-component> is mounted beside it,
// and its data is written back to the textarea on change so form submission is unchanged.
// A "Gennemse billeder og filer" picker provides the asset store (images/files) the old
// CKEditor 4 Browse Server offered.
export default class CkeditorController extends Controller {
  connect() {
    const config = digg(window, "ckeditor5Editor")

    if (!config) {
      console.error("window.ckeditor5Editor is not defined; the CKEditor 5 editor could not be initialized")

      return
    }

    this.assets = []
    this.nextPage = null
    this.loadingAssets = false
    this.uploading = false
    this.uploadKind = "image"
    this.assetErrorMessage = null

    this.textarea = this.element
    this.textarea.style.display = "none"

    const container = document.createElement("div")

    container.className = "ckeditor5-editor-field"
    this.element.insertAdjacentElement("beforebegin", container)
    this.container = container

    const component = document.createElement("ckeditor-component")

    Object.entries(config).forEach(([name, value]) => component.setAttribute(name, String(value)))
    container.appendChild(component)
    this.component = component

    this.mountPicker(container)

    component
      .runAfterEditorReady((editor) => {
        this.editor = editor
        editor.setData(this.textarea.value || "")
        editor.model.document.on("change:data", () => {
          this.textarea.value = editor.getData()
        })
        this.setupUploadAdapter(editor)
      })
      .catch((error) => console.error("CKEditor 5 failed to initialize", error))
  }

  disconnect() {
    // Removing the container disconnects the <ckeditor-component>, which destroys the editor.
    this.container?.remove()
  }

  setupUploadAdapter(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => ({
      upload: async () => {
        const asset = await this.uploadAsset(await loader.file, "image")

        return {default: asset.url}
      },
      abort: () => {
        // Uploads are a single in-flight POST; CKEditor's abort hook has no effect.
      }
    })
  }

  async uploadAsset(file, kind) {
    const formData = new FormData()

    formData.append("upload", file)
    formData.append("kind", kind)
    const result = await EditorPilot.uploadAsset(formData)

    return result.asset
  }

  mountPicker(container) {
    const button = document.createElement("button")

    button.type = "button"
    button.className = "btn btn-secondary ckeditor5-browse-button"
    button.textContent = "Gennemse billeder og filer"
    button.addEventListener("click", () => this.openPicker())
    container.appendChild(button)

    const dialog = document.createElement("dialog")

    dialog.className = "editor-pilot-picker ckeditor5-picker"
    dialog.innerHTML = this.pickerMarkup()
    this.wirePicker(dialog)
    container.appendChild(dialog)
    this.dialog = dialog
  }

  pickerMarkup() {
    return `
      <div class="editor-pilot-picker-header">
        <h2>Billeder og filer</h2>
        <button class="btn btn-secondary ckeditor5-picker-close" type="button">Luk</button>
      </div>
      <form class="editor-pilot-picker-upload">
        <label>
          Type
          <select class="ckeditor5-picker-kind">
            <option value="image">Billede</option>
            <option value="file">Fil</option>
          </select>
        </label>
        <input class="ckeditor5-picker-file" type="file" />
        <button class="btn btn-primary ckeditor5-picker-upload" type="submit">Upload</button>
      </form>
      <p class="editor-pilot-error ckeditor5-picker-error" role="alert" hidden></p>
      <div class="editor-pilot-picker-list"></div>
      <button class="btn btn-secondary ckeditor5-picker-load-more" type="button" hidden>Vis flere</button>
    `
  }

  wirePicker(dialog) {
    dialog.querySelector(".ckeditor5-picker-close").addEventListener("click", () => dialog.close())

    this.form = dialog.querySelector("form")
    this.form.addEventListener("submit", (event) => this.onUploadSubmit(event))

    this.kindSelect = dialog.querySelector(".ckeditor5-picker-kind")
    this.kindSelect.value = this.uploadKind
    this.kindSelect.addEventListener("change", (event) => {
      this.uploadKind = event.target.value
      this.fileInput.accept = this.acceptFor(this.uploadKind)
    })

    this.fileInput = dialog.querySelector(".ckeditor5-picker-file")
    this.fileInput.accept = this.acceptFor(this.uploadKind)

    this.uploadButton = dialog.querySelector(".ckeditor5-picker-upload")
    this.assetError = dialog.querySelector(".ckeditor5-picker-error")
    this.list = dialog.querySelector(".editor-pilot-picker-list")
    this.loadMoreButton = dialog.querySelector(".ckeditor5-picker-load-more")
    this.loadMoreButton.addEventListener("click", () => this.loadAssets(this.nextPage))
  }

  openPicker() {
    this.assets = []
    this.nextPage = null
    this.loadingAssets = true
    this.setAssetError(null)
    this.renderList()
    this.loadAssets(1)
    this.dialog.showModal()
  }

  acceptFor(kind) {
    return kind === "image" ? ".jpg,.jpeg,.png,.gif,.tiff" : ".pdf"
  }

  async loadAssets(page = 1) {
    this.loadingAssets = true
    this.setAssetError(null)
    this.updatePickerButtons()
    try {
      const result = await EditorPilot.listAssets({page})

      this.assets = page === 1 ? result.assets : [...this.assets, ...result.assets]
      this.nextPage = result.next_page
    } catch (error) {
      this.setAssetError(error)
    } finally {
      this.loadingAssets = false
      this.renderList()
      this.updatePickerButtons()
    }
  }

  renderList() {
    this.list.textContent = ""
    this.assets.forEach((asset) => {
      const button = document.createElement("button")

      button.className = "editor-pilot-asset"
      button.type = "button"
      button.dataset.assetId = asset.id
      button.addEventListener("click", () => this.insertAsset(asset))

      if (asset.thumbnail_url) {
        const img = document.createElement("img")

        img.alt = ""
        img.src = asset.thumbnail_url
        button.appendChild(img)
      }
      button.appendChild(document.createTextNode(asset.name))
      this.list.appendChild(button)
    })

    if (this.assets.length === 0 && !this.loadingAssets && !this.assetErrorMessage) {
      const empty = document.createElement("p")

      empty.textContent = "Der er ingen billeder eller filer endnu."
      this.list.appendChild(empty)
    }
  }

  insertAsset(asset) {
    const {editor} = this

    if (!editor) return
    if (asset.kind === "image") {
      editor.execute("insertImage", {source: asset.url})
    } else {
      editor.model.change((writer) => {
        editor.model.insertContent(writer.createText(asset.name, {linkHref: asset.url}), editor.model.document.selection)
      })
    }
    this.dialog.close()
    editor.editing.view.focus()
  }

  async onUploadSubmit(event) {
    event.preventDefault()
    if (this.uploading) return

    const file = this.fileInput.files[0]

    if (!file) return

    this.uploading = true
    this.setAssetError(null)
    this.updatePickerButtons()
    try {
      await this.uploadAsset(file, this.uploadKind)
      this.loadAssets(1)
    } catch (error) {
      this.setAssetError(error)
    } finally {
      this.uploading = false
      this.updatePickerButtons()
    }
  }

  setAssetError(error) {
    this.assetErrorMessage = error ? error.message : null
    this.assetError.textContent = this.assetErrorMessage || ""
    this.assetError.hidden = !this.assetErrorMessage
  }

  updatePickerButtons() {
    this.uploadButton.disabled = this.uploading
    this.uploadButton.textContent = this.uploading ? "Uploader…" : "Upload"
    this.loadMoreButton.disabled = this.loadingAssets
    this.loadMoreButton.hidden = !this.nextPage
  }
}
